import React, { useState } from "react";
import { motion } from "framer-motion";
import SectionHeading from "@/components/common/SectionHeading";
import { processSteps } from "@/config/site";

export default function ProcessTimeline({ chapter = "03", dark = false }) {
  const [active, setActive] = useState(0);
  const bg = dark ? "bg-navy" : "bg-white";

  return (
    <section className={`${dark ? "bg-navy" : "bg-background"}`} data-testid="process-timeline" id="process">
      <div className="container-page py-20 lg:py-28">
        <SectionHeading
          chapter={chapter}
          eyebrow="How it works"
          title="From requirement to delivery coordination"
          intro="A clear, step-by-step path. Select any stage to see what happens and what is expected from you."
          invert={dark}
        />

        {/* Desktop interactive rail */}
        <div className="mt-14 hidden lg:block">
          <div className="relative">
            <div className="absolute top-[14px] left-0 right-0 h-px bg-[#D9E0E4]" />
            <motion.div
              className="absolute top-[14px] left-0 h-px bg-copper"
              animate={{ width: `${(active / (processSteps.length - 1)) * 100}%` }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            />
            <div className="grid grid-cols-6 gap-4">
              {processSteps.map((s, i) => (
                <button
                  key={s.n}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className="text-left group"
                  data-testid={`process-node-${i}`}
                  aria-pressed={active === i}
                >
                  <span
                    className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 text-[11px] font-bold transition-colors ${
                      i <= active
                        ? "bg-copper border-copper text-white"
                        : dark ? "bg-navy border-white/30 text-white/50" : "bg-white border-[#D9E0E4] text-muted-foreground"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <p className={`mt-4 font-heading font-semibold text-[15px] transition-colors ${
                    active === i ? (dark ? "text-white" : "text-navy") : (dark ? "text-white/50" : "text-muted-foreground")
                  }`}>
                    {s.title}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`mt-10 rounded-lg border p-8 ${dark ? "bg-white/[0.04] border-white/10" : "bg-white border-[#D9E0E4]"}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="font-heading font-extrabold text-copper text-[15px]">{processSteps[active].n}</span>
              <h3 className={`font-heading font-bold text-[22px] ${dark ? "text-white" : "text-navy"}`}>{processSteps[active].title}</h3>
            </div>
            <p className={`text-[16px] leading-relaxed max-w-2xl ${dark ? "text-white/70" : "text-muted-foreground"}`}>
              {processSteps[active].copy}
            </p>
          </motion.div>
        </div>

        {/* Mobile vertical timeline */}
        <div className="mt-10 lg:hidden space-y-0">
          {processSteps.map((s, i) => (
            <div key={s.n} className="relative pl-12 pb-8 last:pb-0">
              {i < processSteps.length - 1 && (
                <span className="absolute left-[13px] top-8 bottom-0 w-px bg-[#D9E0E4]" />
              )}
              <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-copper text-white text-[11px] font-bold">
                {i + 1}
              </span>
              <h3 className={`font-heading font-semibold text-[17px] ${dark ? "text-white" : "text-navy"}`}>{s.title}</h3>
              <p className={`mt-1.5 text-[14.5px] leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>{s.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
